import { useState, useCallback } from 'react';
import {
  IconButton,
  MdEdit,
  MdCheck,
  ControllerTextField,
  CircularProgress,
  ClickAwayListener,
  Box,
} from '@bymason-private/legacy-pattern-components';

const noValue = <span>&nbsp;</span>;

export function EditableText({ value, isBusy, maxLength, onChange }: EditableTextProps) {
  const [showEditMode, setShowEditMode] = useState(false);
  const [newValue, setNewValue] = useState(value);
  const handleClickEdit = () => {
    setShowEditMode(true);
  };
  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (evt) => {
    setNewValue(evt.target.value);
  };
  const handleSubmit = useCallback(() => {
    if (newValue !== value) {
      onChange && onChange(newValue);
    }

    setShowEditMode(false);
  }, [newValue, onChange, value]);
  const handleCancel = () => {
    setShowEditMode(false);
  };
  const handleKeyDown = useCallback(
    (evt: React.KeyboardEvent<HTMLInputElement>) => {
      if (evt.key === 'Tab' || evt.key === 'Enter') {
        handleSubmit();
      }

      if (evt.key === 'Escape') {
        handleCancel();
      }
    },
    [handleSubmit]
  );

  return (
    <Box
      className='showCellEditableContainer'
      sx={{
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
      }}
    >
      {showEditMode ? (
        <ClickAwayListener onClickAway={handleCancel}>
          <div>
            <ControllerTextField
              sx={{ margin: 0 }}
              autoFocus
              value={newValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              inputProps={maxLength ? { maxLength } : undefined}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={handleSubmit} title='Click to save'>
                    <MdCheck />
                  </IconButton>
                ),
              }}
            />
          </div>
        </ClickAwayListener>
      ) : (
        <>
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value || noValue}
          </Box>
          {isBusy ? (
            <CircularProgress sx={{ marginLeft: 0.5 }} size='1em' color='secondary' />
          ) : (
            <IconButton
              className='showCellEditableIcon'
              sx={{
                flexShrink: 0,
                width: '20px',
                height: '20px',
                padding: '2px',
                marginLeft: 0.5,
              }}
              onClick={handleClickEdit}
              title='Click icon to edit'
            >
              <MdEdit />
            </IconButton>
          )}
        </>
      )}
    </Box>
  );
}

interface EditableTextProps {
  value: string;
  isBusy?: boolean;
  onChange?: (newValue: string) => void;
  maxLength?: number;
}
